import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { FEEDBACK_KINDS, useSubmitFeedback } from '../model/feedback';
import type { FeedbackKind } from '../model/feedback';

/**
 * The contact form: ideas, problems, working together, money.
 *
 * Four kinds rather than one box, because the four go to different people and the sender
 * knows which one they are. And no account required — the people worth hearing from about
 * cooperation are exactly the ones who have not signed up.
 *
 * Contact is optional even for a business proposal. Refusing the message without it would
 * lose the message, and a proposal with no way back is still information about what people
 * want from this.
 */
export function FeedbackDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<FeedbackKind>('Idea');
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState('');
  const [sent, setSent] = useState(false);

  const submit = useSubmitFeedback();

  /**
   * Clearing happens after the dialog has gone, not when it is asked to go.
   *
   * The two are a fifth of a second apart — the closing animation — and resetting at the
   * start of it re-renders the empty form under the fade, so the last thing a person sees
   * after "sent, thank you" is the blank form they just filled in.
   */
  function forget() {
    setSent(false);
    setMessage('');
    setContact('');
    submit.reset();
  }

  async function send() {
    await submit.mutateAsync({ kind, message, contact: contact.trim() || undefined });
    setSent(true);
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{ transition: { onExited: forget } }}
    >
      <DialogTitle>{t('feedback.title')}</DialogTitle>

      <DialogContent dividers>
        {sent ? (
          <Alert severity="success">{t('feedback.thanks')}</Alert>
        ) : (
          <Stack spacing={2}>
            <DialogContentText variant="body2">{t('feedback.intro')}</DialogContentText>

            {submit.error && <Alert severity="error">{submit.error.message}</Alert>}

            <TextField
              select
              label={t('feedback.kind')}
              value={kind}
              onChange={(event) => setKind(event.target.value as FeedbackKind)}
            >
              {FEEDBACK_KINDS.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`feedback.kinds.${value}`)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              multiline
              minRows={5}
              label={t('feedback.message')}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              slotProps={{ htmlInput: { maxLength: 4000 } }}
            />

            <TextField
              label={t('feedback.contact')}
              helperText={t('feedback.contactHelp')}
              value={contact}
              onChange={(event) => setContact(event.target.value)}
              slotProps={{ htmlInput: { maxLength: 200 } }}
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t(sent ? 'common.close' : 'common.cancel')}</Button>
        {!sent && (
          <Button
            variant="contained"
            // Ten characters is the server's rule too, so the refusal happens before the
            // request rather than after it.
            disabled={submit.isPending || message.trim().length < 10}
            onClick={() => void send()}
          >
            {t('feedback.send')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
